<div class="flex flex-col h-screen">
  <!-- Main Application Header -->
  @if (!isMobileDocumentViewerOpen()) {
    <app-header [doctorName]="doctorName()" [showSearch]="false"></app-header>
  }

  <!-- Main Content with Sidenav -->
  <div class="flex flex-1 overflow-hidden">
    <!-- Sidenav (Consistent height and styling) -->
    @if (caseDetails$ | async) {
      <aside
        class="w-64 bg-white text-gray-900 dark:bg-gray-800 dark:text-white p-4 overflow-y-auto hidden md:block flex-col gap-4"
      >
        <app-case-status-card [caseDetails$]="caseDetails$"></app-case-status-card>
        <app-case-details-nav></app-case-details-nav>
      </aside>
    }

    <!-- Content Area -->
    <main
      class="flex-1 p-4 overflow-y-auto dark:text-gray-200 pb-16 md:pb-2 bg-[url('assets/images/subtle-prism.svg')] dark:bg-[url('assets/images/subtle-prism-dark.svg')] bg-cover bg-no-repeat bg-center"
    >
      @if (caseDetails$ | async; as details) {
        <!-- Patient Details Sticky Bar (when data is loaded) -->
        @if (!isMobileDocumentViewerOpen()) {
          <div
            data-cy="page-caseDetails-div-caseStatusHeader"
            class="sticky top-[0px] z-30 bg-white dark:bg-gray-800 shadow-xl p-4 flex flex-col md:flex-row justify-center md:justify-between flex-wrap md:flex-nowrap dark:border-gray-700 rounded-lg mx-auto max-w-screen-xl w-full mb-2"
          >
            <!-- Desktop Left Buttons (Back) -->
            <div class="hidden md:flex items-center">
              <button
                (click)="closeCaseDetails()"
                [appTooltip]="'Back'"
                data-cy="page-caseDetails-button-back"
                class="flex items-center text-gray-700 dark:text-gray-300 mr-6 cursor-pointer"
              >
                <span class="material-symbols-outlined mr-1">chevron_left</span>
                Back
              </button>
            </div>

            <!-- Patient Name, Age, Gender, Org Name, Case Type, Details -->
            <div class="flex flex-col md:flex-row md:items-center md:space-x-4">
              <h1 class="text-lg text-gray-800 dark:text-white" data-cy="page-caseDetails-text-patientName">
                {{ details?.name ?? details?.firstName + ' ' + details?.lastName }}
                {{ formatPatientSubheader(details) }}
              </h1>
              <span class="text-xs text-gray-600 dark:text-gray-400" data-cy="page-caseDetails-text-caseInfo">
                {{ formatCaseInfo(details) }}
              </span>
            </div>

            <!-- Desktop Right Buttons (Mark as Seen / Seen Badge + Check for Updates) -->
            <div class="hidden md:flex items-center space-x-3">
              @if (canMarkAsSeen()) {
                <button
                  (click)="onMarkAsSeen()"
                  [disabled]="isActionLoading()"
                  [appTooltip]="'Mark patient evaluation as Seen'"
                  data-cy="page-caseDetails-button-markAsSeen"
                  class="flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white font-medium py-1 px-3 rounded-md transition shadow-sm disabled:opacity-50 cursor-pointer"
                >
                  @if (isActionLoading()) {
                    <span class="material-symbols-outlined animate-spin text-sm">progress_activity</span>
                    <span>Saving...</span>
                  } @else {
                    <span class="material-symbols-outlined text-sm">check_circle</span>
                    <span>Mark as Seen</span>
                  }
                </button>
              } @else if (isSeen()) {
                <div class="flex items-center gap-2">
                  <span
                    data-cy="page-caseDetails-badge-seen"
                    class="inline-flex items-center gap-1 bg-emerald-100 text-emerald-800 dark:bg-emerald-900/60 dark:text-emerald-300 font-semibold px-2.5 py-1 rounded-full text-xs"
                  >
                    <span class="material-symbols-outlined text-xs">check</span>
                    Status: Seen
                    @if (activeAppointment()?.updateTime) {
                      ({{ activeAppointment()?.updateTime | date: 'shortTime' }})
                    }
                  </span>
                  @if (canUndoSeen()) {
                    <button
                      (click)="onUndoSeen()"
                      [disabled]="isActionLoading()"
                      [appTooltip]="'Undo marking as Seen'"
                      data-cy="page-caseDetails-button-undoSeen"
                      class="text-xs text-rose-600 dark:text-rose-400 hover:text-rose-700 dark:hover:text-rose-300 flex items-center gap-0.5 cursor-pointer disabled:opacity-50"
                    >
                      @if (isActionLoading()) {
                        <span class="material-symbols-outlined animate-spin text-xs">progress_activity</span>
                      } @else {
                        <span class="material-symbols-outlined text-xs">undo</span>
                        <span>Undo</span>
                      }
                    </button>
                  }
                </div>
              }

              <button
                (click)="refreshCaseDetails()"
                [appTooltip]="'Fetch updated patient details'"
                data-cy="page-caseDetails-button-refresh"
                class="cursor:pointer flex items-center gap-2 border border-sky-600 text-sky-600 dark:text-white py-1 px-2 rounded-md cursor-pointer"
              >
                <span class="material-symbols-outlined">cloud_sync</span>Check for Updates
              </button>
            </div>

            <!-- Mobile Buttons -->
            <div class="md:hidden flex flex-col items-center gap-2 w-full mt-2 pt-2 border-t border-gray-100 dark:border-gray-700">
              @if (canMarkAsSeen()) {
                <button
                  (click)="onMarkAsSeen()"
                  [disabled]="isActionLoading()"
                  data-cy="page-caseDetails-button-markAsSeenMobile"
                  class="w-full flex items-center justify-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white font-medium py-1.5 px-3 rounded-md transition shadow-sm disabled:opacity-50 cursor-pointer text-sm"
                >
                  @if (isActionLoading()) {
                    <span class="material-symbols-outlined animate-spin text-sm">progress_activity</span>
                    <span>Saving...</span>
                  } @else {
                    <span class="material-symbols-outlined text-sm">check_circle</span>
                    <span>Mark as Seen</span>
                  }
                </button>
              } @else if (isSeen()) {
                <div class="flex items-center justify-center gap-2">
                  <span
                    data-cy="page-caseDetails-badge-seenMobile"
                    class="inline-flex items-center gap-1 bg-emerald-100 text-emerald-800 dark:bg-emerald-900/60 dark:text-emerald-300 font-semibold px-2.5 py-1 rounded-full text-xs"
                  >
                    <span class="material-symbols-outlined text-xs">check</span>
                    Status: Seen
                    @if (activeAppointment()?.updateTime) {
                      ({{ activeAppointment()?.updateTime | date: 'shortTime' }})
                    }
                  </span>
                  @if (canUndoSeen()) {
                    <button
                      (click)="onUndoSeen()"
                      [disabled]="isActionLoading()"
                      data-cy="page-caseDetails-button-undoSeenMobile"
                      class="text-xs text-rose-600 dark:text-rose-400 hover:text-rose-700 dark:hover:text-rose-300 flex items-center gap-0.5 cursor-pointer disabled:opacity-50"
                    >
                      @if (isActionLoading()) {
                        <span class="material-symbols-outlined animate-spin text-xs">progress_activity</span>
                      } @else {
                        <span class="material-symbols-outlined text-xs">undo</span>
                        <span>Undo</span>
                      }
                    </button>
                  }
                </div>
              }

              <div class="flex justify-center space-x-12 p-1">
                <button
                  (click)="openCaseStatusDialog()"
                  [appTooltip]="'Case Status'"
                  data-cy="page-caseDetails-button-mobileCaseStatus"
                  class="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <p class="flex items-center gap-1">
                    <span class="material-symbols-outlined text-gray-600 dark:text-gray-400">info</span> Case Status
                  </p>
                </button>
                <button
                  (click)="refreshCaseDetails()"
                  [appTooltip]="'Refresh'"
                  data-cy="page-caseDetails-button-refresh"
                  class="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <span class="material-symbols-outlined text-gray-600 dark:text-gray-400">cloud_sync</span>
                </button>
                <button
                  (click)="closeCaseDetails()"
                  [appTooltip]="'Close'"
                  data-cy="page-caseDetails-button-close"
                  class="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <span class="material-symbols-outlined text-gray-600 dark:text-gray-400">close</span>
                </button>
              </div>
            </div>
          </div>
        }
        <router-outlet></router-outlet>
      } @else {
        <!-- Loading state (full height of main content area) -->
        <div
          class="flex flex-col items-center justify-center h-full w-full text-center"
          data-cy="page-caseDetails-loadingState"
        >
          <app-loading-spinner [isLoading]="isLoadingData()"></app-loading-spinner>
          <p class="text-lg text-gray-700 dark:text-gray-300 mb-4 mt-4">Please wait while we retrieve your records.</p>
          <p class="text-sm text-gray-500 dark:text-gray-400 mb-6">
            If it takes too long, contact the facility for assistance.
          </p>
          <button
            (click)="closeCaseDetails()"
            data-cy="page-caseDetails-button-backToDashboard"
            class="flex items-center px-4 py-2 border border-gray-300 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <span class="material-symbols-outlined mr-2">arrow_back</span>
            Back to Dashboard
          </button>
        </div>
      }
    </main>
  </div>

  <!-- Bottom Nav for Mobile -->
  @if (caseDetails$ | async) {
    <div
      class="md:hidden bg-white text-gray-900 dark:bg-gray-800 dark:text-white p-2 shadow-lg fixed bottom-0 left-0 w-full z-10"
    >
      <app-case-details-nav [isMobileNav]="true"></app-case-details-nav>
    </div>
  }
</div>
